<template>
  <div :class="wrapperClass">
    <template v-if="marketing || editing">
      <div
        v-if="allowCurator && curatorLoading"
        class="social-feed-wrapper__curator-status"
      >
        Loading posts…
      </div>
      <div
        v-else-if="allowCurator && curatorError && !curatorUsesFallback"
        class="social-feed-wrapper__curator-status social-feed-wrapper__curator-status--error"
      >
        Unable to load posts at this time.
      </div>

      <!-- Carousel Layout -->
      <div
        v-else-if="layout === 'carousel'"
        class="social-feed-wrapper__carousel"
      >
        <div
          ref="carouselTrack"
          class="social-feed-wrapper__carousel-track"
          :style="carouselStyle"
        >
          <template v-if="curatorActive">
            <social-media-card
              v-for="(post, index) in curatorPosts"
              :key="post.id || index"
              mode="custom"
              :platform="post.platform"
              :author="post.author"
              :author-avatar="post.avatar"
              :date="post.date"
              :image="post.image"
              :image-alt="post.author || 'Post'"
              :link="post.link"
              :link-text="curatorLinkText"
              :hashtags="post.hashtags"
              :text-clamp="6"
            >
              <template #text>{{ post.text }}</template>
            </social-media-card>
          </template>
          <slot v-else />
        </div>
        <div class="social-feed-wrapper__carousel-controls">
          <button
            class="social-feed-wrapper__nav social-feed-wrapper__nav--prev"
            :disabled="!canScrollLeft"
            aria-label="Previous"
            @click="scrollCarousel('prev')"
          >
            <svg><use xlink:href="#chevron-left" /></svg>
          </button>
          <button
            class="social-feed-wrapper__nav social-feed-wrapper__nav--next"
            :disabled="!canScrollRight"
            aria-label="Next"
            @click="scrollCarousel('next')"
          >
            <svg><use xlink:href="#chevron-right" /></svg>
          </button>
        </div>
      </div>

      <!-- Grid Layout -->
      <div v-else-if="layout === 'grid'">
        <component :is="gridItems" />

        <div
          v-if="hasMore"
          class="social-feed-wrapper__load-more"
        >

          <slot
            name="load-more"
            :load-more="loadMore"
            :remaining="remaining"
            :visible-count="visibleCount"
            :total="total"
          >
            <c-button
              href="#"
              :radius="50"
              bg-color="#9ddda9"
              color="#1d2822"
              @click.native.prevent="loadMore"
            >
              {{ loadMoreText }}
            </c-button>
          </slot>
        </div>
      </div>
    </template>

    <!-- OneTrust -->
    <div v-else>
      <onetrust-blocker></onetrust-blocker>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CButton from '../../00_v2/button/button.vue'
import SocialMediaCard from '../../03_molecules/social-media-card/social-media-card.vue'


const GAP = '24px'
const COLUMNS = { mobile: 1, tablet: 2, desktop: 4 }
const CURATOR_TEXT_CLAMP = 6

export default {
  name: 'social-feed-wrapper',
  components: {
    CButton,
    SocialMediaCard,
  },
  props: {
    layout: {
      type: String,
      default: 'grid',
      validator(value) {
        return ['grid', 'carousel'].includes(value)
      },
    },
    showNavigation: {
      type: Boolean,
      default: true,
    },
    autoPlay: {
      type: Boolean,
      default: false,
    },
    showLoadMore: {
      type: Boolean,
      default: false,
    },
    loadMoreText: {
      type: String,
      default: 'See more',
    },
    backgroundColor: {
      type: String,
      default: 'transparent',
    },
    allowCurator: {
      type: Boolean,
      default: false,
    },
    curatorFeedId: {
      type: String,
      default: process.env.CURATOR_FEED_ID || '',
    },
    curatorLinkText: {
      type: String,
      default: 'View post',
    },
    curatorFallbackToSlot: {
      type: Boolean,
      default: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentScroll: 0,
      maxScroll: 0,
      autoPlayTimer: null,
      visibleCount: 8,
      curatorPosts: [],
      curatorLoading: this.allowCurator,
      curatorError: false,
      curatorLoaded: false,
    }
  },
  computed: {
    ...mapGetters('onetrust', [
      'marketing'
    ]),
    wrapperClass() {
      return [
        'social-feed-wrapper',
        `social-feed-wrapper--${this.layout}`,
      ]
    },
    gridStyle() {
      return {
        '--columns-mobile': COLUMNS.mobile,
        '--columns-tablet': COLUMNS.tablet,
        '--columns-desktop': COLUMNS.desktop,
        '--gap': GAP,
        backgroundColor: this.backgroundColor,
        padding: '0',
      }
    },
    carouselStyle() {
      return {
        gap: GAP,
      }
    },
    canScrollLeft() {
      return this.currentScroll > 0
    },
    canScrollRight() {
      return this.currentScroll < this.maxScroll
    },

    slotCards() {
      const nodes = this.$slots.default || []
      return nodes.filter(node => node.tag)
    },
    curatorActive() {
      return (
        this.allowCurator &&
        this.curatorLoaded &&
        !this.curatorError &&
        this.curatorPosts.length > 0
      )
    },

    curatorUsesFallback() {
      return (
        this.allowCurator &&
        this.curatorFallbackToSlot &&
        !this.curatorLoading &&
        !this.curatorActive &&
        this.slotCards.length > 0
      )
    },
    total() {
      return this.curatorActive ? this.curatorPosts.length : this.slotCards.length
    },
    lazyEnabled() {
      return this.layout === 'grid' && this.showLoadMore
    },
    hasMore() {
      return this.lazyEnabled && this.visibleCount < this.total
    },
    remaining() {
      return Math.max(0, this.total - this.visibleCount)
    },
    // Functional component that renders the visible grid cards
    // (Curator posts when active, otherwise the slot cards).
    gridItems() {
      const gridStyle = this.gridStyle
      const curatorActive = this.curatorActive
      const linkText = this.curatorLinkText
      const textClamp = CURATOR_TEXT_CLAMP
      const posts = this.lazyEnabled
        ? this.curatorPosts.slice(0, this.visibleCount)
        : this.curatorPosts
      const slotCards = this.lazyEnabled
        ? this.slotCards.slice(0, this.visibleCount)
        : this.slotCards
      return {
        render(h) {
          const children = curatorActive
            ? posts.map((post, index) => h(SocialMediaCard, {
              key: post.id || index,
              props: {
                mode: 'custom',
                platform: post.platform,
                author: post.author,
                authorAvatar: post.avatar,
                date: post.date,
                image: post.image,
                imageAlt: post.author || 'Post',
                link: post.link,
                linkText,
                hashtags: post.hashtags,
                textClamp,
              },
              scopedSlots: {
                text: () => post.text,
              },
            }))
            : slotCards
          return h('div', { class: 'social-feed-wrapper__grid', style: gridStyle }, children)
        },
      }
    },
  },
  watch: {
    marketing(consented) {
      if (consented && this.allowCurator) {
        this.$nextTick(() => this.fetchCuratorPosts())
      }
      if (consented && this.layout === 'carousel') {
        this.$nextTick(() => this.updateCarouselDimensions())
        if (this.autoPlay) {
          this.startAutoPlay()
        }
      }
    },
  },
  mounted() {
    if (this.allowCurator && this.marketing) {
      this.$nextTick(() => this.fetchCuratorPosts())
    }
    if (this.layout === 'carousel') {
      this.$nextTick(() => this.updateCarouselDimensions())
      window.addEventListener('resize', this.updateCarouselDimensions)
      window.addEventListener('load', this.updateCarouselDimensions)

      if (this.autoPlay) {
        this.startAutoPlay()
      }
    }
  },
  beforeDestroy() {
    if (this.layout === 'carousel') {
      window.removeEventListener('resize', this.updateCarouselDimensions)
      window.removeEventListener('load', this.updateCarouselDimensions)
      this.stopAutoPlay()
    }
  },
  methods: {
    updateCarouselDimensions() {
      const track = this.$refs.carouselTrack
      if (!track || !track.children.length) return

      const gapPx = parseInt(GAP, 10) || 24
      const viewportWidth = track.clientWidth
      let cols = COLUMNS.mobile
      if (viewportWidth >= 1200) {
        cols = COLUMNS.desktop
      } else if (viewportWidth >= 768) {
        cols = COLUMNS.tablet
      }
      cols = Math.max(1, cols)
      const itemWidth = (viewportWidth - gapPx * (cols - 1)) / cols

      Array.from(track.children).forEach(child => {
        child.style.width = `${itemWidth}px`
        child.style.flexShrink = '0'
      })

      this.maxScroll = Math.max(0, track.scrollWidth - viewportWidth)

      if (this.currentScroll > this.maxScroll) {
        this.currentScroll = this.maxScroll
        track.scrollLeft = this.currentScroll
      }
    },
    scrollCarousel(direction) {
      const track = this.$refs.carouselTrack
      if (!track) return

      const firstItem = track.children[0]
      const itemWidth = firstItem ? firstItem.offsetWidth + (parseInt(GAP, 10) || 24) : 300

      if (direction === 'prev') {
        this.currentScroll = Math.max(0, this.currentScroll - itemWidth)
      } else {
        this.currentScroll = Math.min(this.maxScroll, this.currentScroll + itemWidth)
      }

      track.scrollLeft = this.currentScroll
      this.$emit('scroll', { direction, currentScroll: this.currentScroll })
    },
    startAutoPlay() {
      this.autoPlayTimer = setInterval(() => {
        if (this.currentScroll >= this.maxScroll) {
          this.currentScroll = 0
          this.$refs.carouselTrack.scrollLeft = 0
        } else {
          this.scrollCarousel('next')
        }
      }, 5000)
    },
    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer)
        this.autoPlayTimer = null
      }
    },
    loadMore() {
      this.visibleCount = Math.min(this.total, this.visibleCount + this.loadMoreStep)
      this.$emit('load-more', {
        visibleCount: this.visibleCount,
        remaining: this.remaining,
        total: this.total,
      })
    },
    fetchCuratorPosts() {
      if (!this.curatorFeedId && !this.curatorApiUrl) {
        this.curatorError = true
        this.curatorLoading = false
        return
      }
      this.curatorLoading = true
      this.curatorError = false
      const url = this.curatorApiUrl
        || `https://api.curator.io/v1.1/feeds/${this.curatorFeedId}/posts?limit=${this.curatorLimit}`
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          return response.json()
        })
        .then(data => {
          const posts = Array.isArray(data) ? data : (data.posts || data.data || [])
          this.curatorPosts = posts.slice(0, this.curatorLimit).map(post => this.mapCuratorPost(post))
          this.curatorLoaded = true
          this.$emit('curator-loaded', { total: this.curatorPosts.length })
        })
        .catch(error => {
          this.curatorError = true
          this.$emit('curator-error', error)
          // eslint-disable-next-line no-console
          console.error('[social-feed-wrapper] Failed to load Curator.io posts:', error)
        })
        .then(() => {
          this.curatorLoading = false
          if (this.layout === 'carousel') {
            this.$nextTick(() => this.updateCarouselDimensions())
          }
        })
    },
    mapCuratorPost(raw) {
      const networkName = (raw.network_name || raw.source_name || raw.network || '').toString().toLowerCase()
      const platform = ['linkedin', 'instagram', 'twitter', 'facebook', 'youtube']
        .find(name => networkName.indexOf(name) !== -1) || ''
      const text = raw.text || raw.caption || raw.message || ''
      const hashtags = (text.match(/#[\wÀ-ÿ]+/g) || []).map(tag => tag.slice(1))
      return {
        id: raw.id || raw.post_id,
        platform,
        author: raw.user_full_name || raw.user_name || raw.user_screen_name || '',
        avatar: raw.user_image || raw.user_profile_image || '',
        date: raw.source_created_at || raw.created_at || '',
        image: raw.image || raw.image_url || raw.thumbnail || '',
        link: raw.url || raw.source_url || raw.post_url || '',
        text,
        hashtags,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.social-feed-wrapper {
  width: 100%;
  position: relative;

  &__curator-status {
    width: 100%;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: #3b3b3b;
    font-size: 0.875rem;
    text-align: center;

    &--error {
      color: #b00020;
    }
  }

  &__carousel {
    position: relative;
    background: #ffffff;
    height: 100%;
    overflow: hidden;
  }

  &__carousel-track {
    display: flex;
    flex-wrap: nowrap;
    align-items: stretch;
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    gap: 0;

    &::-webkit-scrollbar {
      display: none;
    }

    ::v-deep .social-media-card {
      max-width: none;
      min-width: 0;
    }

    ::v-deep .social-media-card__imageContainer,
    ::v-deep .social-media-card__videoContainer {
      max-width: none;
      min-width: 0;
    }
  }

  &__carousel-controls {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    z-index: 20;
  }

  &__nav {
    position: absolute;
    top: 20%;
    background: #9ddda9;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: all;
    flex-shrink: 0;
    z-index: 20;
    width: 2.5rem;
    height: 2.5rem;
    transition: background 0.2s ease, left 0.2s ease-in, right 0.2s ease-in;

    @media (min-width: 768px) {
      width: 6.5rem;
      height: 5rem;
      top: 50%;
      transform: translateY(-50%);
    }

    &:hover:not(:disabled) {
      background: #8a9984;
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    svg {
      width: 1rem;
      height: 1rem;
      fill: #1d2822;
      color: #1d2822;
      display: block;

      @media (min-width: 768px) {
        width: 2.0625rem;
        height: 2.0625rem;
      }
    }

    &--prev {
      left: 0;
      border-radius: 0.125rem 6.25rem 6.25rem 0.125rem;

      @media (min-width: 768px) {
        left: -4.0625rem;

        &:hover:not(:disabled) {
          left: 0;
        }
      }
    }

    &--next {
      right: 0;
      border-radius: 6.25rem 0.125rem 0.125rem 6.25rem;

      @media (min-width: 768px) {
        right: -4.0625rem;

        &:hover:not(:disabled) {
          right: 0;
        }
      }
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(var(--columns-mobile), 1fr);
    gap: var(--gap);

    @media (min-width: 768px) {
      grid-template-columns: repeat(var(--columns-tablet), 1fr);
    }

    @media (min-width: 1200px) {
      grid-template-columns: repeat(var(--columns-desktop), 1fr);
    }

    ::v-deep .social-media-card {
      max-width: 100%;
      min-width: 0;
    }

    ::v-deep .social-media-card__imageContainer,
    ::v-deep .social-media-card__videoContainer {
      max-width: 100%;
      min-width: 0;
    }
  }

  &__load-more {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
  }
}
</style>
